/**
 * ComputeNode entity for managing compute environments.
 * Represents a sandboxed execution environment (local machine, E2B, etc.)
 *
 * Session Ownership (Frontend-only):
 * ComputeNode owns the frontend session cache. Sessions are stored per-node and
 * represent the local view of PTY sessions on this compute node. When the active
 * ComputeNode changes, the frontend cache is reset (NOT the backend PTYs).
 */

import { APIEntity, dataManager, registerEntity } from '../../APIEntity';
import { isApiError } from '../../ApiResponse';
import { TypeId } from '../../models/TypeId';
import type { IAgenticProcess } from '../../process/agentic-process';
import {
  FlowData,
  FlowElementTypes,
  FlowEvents,
  FlowStreamProcessor,
  ShellCmdProgress,
  ShellCommandProcessor,
  ShellInputFlowData,
  ShellOutputFlowData,
} from '../../flow_processing';
import { EntityMerge } from '../../IEntity';
import { ActionInfo } from '../../models';
import { hostTerminalTheme } from '../../utils/runtime';
import {
  type AppUpgrade,
  ComputeProviderType,
  type NodeStatus,
  type ProvisionResult,
  type ProvisionSetup,
  RuntimeEnvironment,
  SANDBOX_PROVIDERS,
  type WorkspaceReady,
  IComputeNode,
} from './compute-node-types';
import type { ComputeNodeSize, MachineStatus, ProcessInfo } from './machine-status';
import { ServiceControlError, type ServiceRuntimeDescriptor } from './service-control';
import { Shell } from '../shell';
import type { NodeHealth } from '../service-endpoint';
import { GitWorkdir } from '../git-workdir';

/**
 * The `node_config` marker the sandbox UI writes at create time to mean "this box
 * is a workspace someone opens", as opposed to an agent's deployment box.
 */
export const WORKSPACE_FLAVOR = 'workspace';

/** Callback for when a new machine session is detected */

/** CLI worker kind shared across resolver APIs. */
export type WorkerKind = 'claude' | 'codex' | 'copilot' | 'opencode';

/** Descriptor returned by {@link ComputeNode.findSession} on hit. */
export interface FindSessionResult {
  session_id: string;
  worker_type: WorkerKind;
  transcript_path: string | null;
  cwd: string | null;
  project_id: string | null;
  session_name: string | null;
}

/**
 * Convert a VFS-relative path to an OS absolute path.
 * VFS paths are relative (no leading slash, no drive letter) like "Users/bob/Flowpad workspace".
 * This function combines them with the OS root and normalizes slashes for the target OS.
 *
 * @param vfsPath - VFS-relative path (e.g., "Users/bob/Flowpad workspace/.flow/system_skills")
 * @param root - OS filesystem root (e.g., "C:\\" on Windows, "/" on Unix)
 * @returns OS absolute path with normalized slashes
 */
export function vfsToOsPath(vfsPath: string, root: string): string {
  if (!vfsPath) return root;
  const combined = `${root}${vfsPath}`;
  // Detect OS from root: Windows roots contain backslash or colon (e.g., "C:\")
  const isWindows = root.includes('\\') || (root.length >= 2 && root[1] === ':');
  // Normalize slashes: backslash for Windows, forward slash for Unix
  return isWindows ? combined.replace(/\//g, '\\') : combined.replace(/\\/g, '/');
}

/**
 * Declaration merge: `implements IComputeNode` only CHECKS the class, it adds no
 * members — so every field declared solely on IComputeNode read as "does not exist
 * on type ComputeNode", even though `deepAssign` populates them from the wire.
 * This interface makes them part of the class type.
 */
// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface ComputeNode extends EntityMerge<IComputeNode> {}

/** One agent placed on a machine, as `compute_node/<id>/agents` answers it. */
export interface NodeAgent {
  typeid: string;
  name: string;
  deployment_typeid: string;
  /** The project holding the agent, to open it there; blank when unknown. */
  project_id: string;
}

/**
 * ComputeNode entity class.
 * Manages sandboxed execution environments for running commands and processes.
 */
@registerEntity
export class ComputeNode extends APIEntity<ComputeNode> implements IComputeNode {
  name: string = '';
  runtime: RuntimeEnvironment = { name: '' };
  node_provider_type?: ComputeProviderType;
  node_provider_id?: string;
  node_config?: Record<string, unknown>;
  node_size?: ComputeNodeSize;
  fs_storage_mount_path?: string | null;
  home_dir?: string | null;
  auto_login?: boolean;
  allow_login?: boolean;
  login_roles?: string[];
  logged_in_user?: string | null;
  static type: string = 'compute_node';

  /**
   * Frontend-only session cache.
   * Sessions are owned by this ComputeNode and represent the local view of PTY sessions.
   * This cache is reset when switching to a different ComputeNode (NOT affecting backend PTYs).
   */
  private sessions: Map<string, Shell> = new Map();

  /** Track known machine session IDs to detect new ones */
  private knownMachineSessions: Set<string> = new Set();

  /** Callback for when a new machine session is detected */

  /** Bound handler for WebSocket data ops (for cleanup) */

  constructor(entity: Partial<IComputeNode> = {}) {
    super(entity);
    this.name = entity.name || '';
    this.runtime = entity.runtime || { name: '' };
    this.node_provider_type = entity.node_provider_type;
    this.node_provider_id = entity.node_provider_id;
    this.node_config = entity.node_config;
    this.node_size = entity.node_size;
    this.fs_storage_mount_path = entity.fs_storage_mount_path ?? undefined;
    this.home_dir = entity.home_dir ?? undefined;
    // Declaring these as class fields is NOT enough to carry them. Under the
    // app's `useDefineForClassFields: true`, a declared-but-unassigned field is
    // DEFINED as `undefined` at construction — overwriting whatever `super`
    // copied off the payload. So the server sent `logged_in_user`, the entity
    // dropped it, and every card rendered "signed out". Every other field here
    // is assigned for the same reason; these two were simply missed.
    this.auto_login = entity.auto_login;
    this.allow_login = entity.allow_login;
    this.login_roles = entity.login_roles;
    this.logged_in_user = entity.logged_in_user;
  }

  /**
   * Resolve the singleton `@local` compute node — "this machine".
   *
   * The robust frontend counterpart to the backend `ComputeNode.get_local()`.
   * Resolution order (cheap → authoritative):
   *   1. the current context compute node, when it is the @local one
   *   2. the bootstrap-issued `default_compute_node`
   *   3. a fetch by the `@local` alias typeid (server resolves it)
   *
   * This is a READ: minting the node is a backend concern (the client cannot
   * create entities). The backend self-heals on any action that touches @local
   * — including {@link Project.getComputeNode} — so a missing row is recreated
   * server-side rather than here. Returns null only if the backend has no
   * @local node AND cannot resolve the alias (should not happen in local mode).
   */
  static async getLocal(): Promise<ComputeNode | null> {
    const { dataContext } = await import('../../FlowSync/context');
    const current = dataContext.computeNode;
    // The context node is @local unless a cloud/sandbox node is active. Treat a
    // local_machine-provider node as @local; otherwise fall through.
    if (current && current.node_provider_type === ComputeProviderType.LOCAL_MACHINE) {
      return current;
    }
    const fromBootstrap = dataContext.bootstrapInfo?.default_compute_node;
    if (fromBootstrap) {
      const node = new ComputeNode(fromBootstrap as any);
      node.markAsExpanded();
      return node;
    }
    try {
      return await dataManager.getByTypeId<ComputeNode>(new TypeId(ComputeNode.type, '@local'));
    } catch {
      return null;
    }
  }

  /**
   * Create a new idle AgenticProcess on this ComputeNode.
   *
   * @param context - Execution context (workdir, permissionMode, model, etc.)
   * @param options - Optional result metadata and watch settings
   * @returns Promise resolving to a new AgenticProcess in NEW status
   *
   * @example
   * ```typescript
   * const computeNode = await ComputeNode.getLocal();
   * const process = await computeNode.createProcess({ workdir: '/path' });
   * await process.start();
   * ```
   */
  async createProcess(
    context: import('../../process/agentic-context').AgenticContext = {},
    options?: {
      result?: { uname?: string; resultType?: string; sourceSessionId?: string };
      watchProcess?: boolean;
      visible?: boolean;
      /**
       * Transport intent for the new session: true → interactive PTY (default
       * when omitted), false → headless JSON-stream (no PTY/xterm). Omitted →
       * the backend defaults to true, preserving today's behaviour.
       */
      pty_mode?: boolean;
      /**
       * First prompt to seed onto the process's queue server-side, BEFORE the
       * visible auto-start. The worker then boots with it as its launch
       * instruction (deterministic — no post-spawn stdin race). Used by
       * {@link AgenticProcess.openTab}.
       */
      launchPrompt?: string;
      /** Tab title for the new process, pinned like a user rename. */
      title?: string;
    },
  ): Promise<import('../../process/agentic-process').AgenticProcess> {
    const { serializeAgenticContext } = await import('../../process/agentic-context');

    const action = new ActionInfo('createProcess', ComputeNode.type, this.id, 'POST');
    action.bodyParameters = {
      context: serializeAgenticContext(context),
      ...(options?.result
        ? {
            result: {
              uname: options.result.uname,
              resultType: options.result.resultType,
              sourceSessionId: options.result.sourceSessionId,
            },
          }
        : {}),
      ...(options?.visible !== undefined ? { visible: options.visible } : {}),
      ...(options?.pty_mode !== undefined ? { pty_mode: options.pty_mode } : {}),
      ...(options?.launchPrompt ? { launch_prompt: options.launchPrompt } : {}),
      ...(options?.title?.trim() ? { title: options.title.trim() } : {}),
      // This action spawns the PTY server-side for a visible process, so the
      // theme has to ride the CREATE — the later `open` only reattaches.
      ...(hostTerminalTheme() ? { theme: hostTerminalTheme() } : {}),
    };

    const response = await dataManager.callAction<unknown, IAgenticProcess>(action);

    const process = dataManager.updateEntityFromJson<import('../../process/agentic-process').AgenticProcess>(response);
    process._context = context;

    // A lease, not a bare watch: the caller gets no release to keep, so the process's
    // end gives the count back (AgenticProcess.watchWhileLive).
    if (options?.watchProcess !== false) {
      await process.watchWhileLive();
    }

    return process;
  }

  /**
   * Resolve a session id to its on-disk descriptor.
   *
   * Pure read-only lookup: never creates an AgenticProcess. Use
   * `AgenticProcess.getByWorkerId(id)` for the find+open flow.
   *
   * @param sessionId - UUID/thread id.
   * @param workerType - Optional hint to skip the other indexer.
   * @returns Descriptor on hit, `null` on 404 (session not found in either history).
   */
  async findSession(sessionId: string, workerType?: WorkerKind): Promise<FindSessionResult | null> {
    const action = new ActionInfo('findSession', ComputeNode.type, this.id, 'GET');
    action.queryParameters = {
      session_id: sessionId,
      ...(workerType ? { worker_type: workerType } : {}),
    };
    try {
      return await dataManager.callAction<void, FindSessionResult>(action);
    } catch (e) {
      if (isApiError(e) && e.response?.status === 404) return null;
      throw e;
    }
  }

  // ============================================================
  // Session Management (Frontend-only cache)
  // ============================================================

  appendSession(session: Shell): void {
    session.compute_node_id = this.id;
    this.sessions.set(session.id, session);
  }

  /**
   * Get a session from this node's frontend cache.
   * @param sessionId - Session identifier
   * @returns ShellSession or undefined if not found
   */
  getSession(sessionId: string): Shell | undefined {
    return this.sessions.get(sessionId);
  }

  /**
   * Check if a session exists in this node's frontend cache.
   * @param sessionId - Session identifier
   * @returns true if session exists
   */
  hasSession(sessionId: string): boolean {
    return this.sessions.has(sessionId);
  }

  /**
   * Remove a session from this node's frontend cache.
   * Note: This does NOT close the backend PTY - it only removes the frontend cache entry.
   * @param sessionId - Session identifier
   * @returns true if session was removed
   */
  removeSession(sessionId: string): boolean {
    return this.sessions.delete(sessionId);
  }

  /**
   * Re-key a session from oldId to newId, preserving Map insertion order.
   * Marks the new ID as known so the machine session watcher won't duplicate it.
   */
  rekeySession(oldId: string, newId: string): boolean {
    if (!this.sessions.has(oldId)) return false;

    const entries = Array.from(this.sessions.entries());
    this.sessions.clear();
    for (const [key, value] of entries) {
      this.sessions.set(key === oldId ? newId : key, value);
    }

    this.knownMachineSessions.delete(oldId);
    this.knownMachineSessions.add(newId);
    return true;
  }

  /**
   * Get all sessions from this node's frontend cache.
   * @returns Array of ShellSession sorted by creation time
   */
  getAllSessions(): Shell[] {
    return Array.from(this.sessions.values()).sort((a, b) => {
      const aTime = a.created_at ? new Date(a.created_at).getTime() : 0;
      const bTime = b.created_at ? new Date(b.created_at).getTime() : 0;
      return aTime - bTime;
    });
  }

  /**
   * Clear all sessions from this node's frontend cache.
   * Note: This does NOT close backend PTYs - it only clears the frontend cache.
   * Used when switching to a different ComputeNode.
   */
  clearLocalSessions(): void {
    this.sessions.clear();
  }

  /**
   * Add a session directly to this node's frontend cache.
   * Used when syncing sessions from backend.
   * @param session - ShellSession to add
   */
  addSession(session: Shell): void {
    session.compute_node_id = this.id;
    this.sessions.set(session.id, session);
  }

  /**
   * Get the number of sessions in this node's frontend cache.
   */
  get sessionCount(): number {
    return this.sessions.size;
  }

  /**
   * Setup the compute node provider.
   * This initializes the compute provider and sets the node_provider_id.
   * Must be called after creating/saving the node before executing commands.
   *
   * @returns Promise with provider node ID
   *
   * @example
   * ```typescript
   * const computeNode = new ComputeNode({
   *   name: 'my-node',
   *   runtime: { name: 'test' },
   *   node_provider_type: ComputeProviderType.LOCAL_MACHINE
   * });
   * await computeNode.save();
   * await computeNode.setup(); // Initialize provider
   * ```
   */
  async setup(body?: Record<string, unknown>): Promise<string> {
    const data = await this.ops<string>('setup', body);

    if (!data) {
      throw new Error('Failed to setup compute node: No response data');
    }

    // Update local provider_id from response
    this.node_provider_id = data;

    return data;
  }

  /**
   * Call one `ops/<op>` command on this node.
   *
   * THE single place a client builds an ops URL. Callers used to hand-roll
   * `new ActionInfo('ops', ...)` wherever they needed one — `use-sandboxes.ts`
   * carried its own copy and called it for eleven different commands, which is
   * why `executeCommand` below had no callers outside tests despite doing the
   * same thing. One spelling, so a change to the action name or the envelope
   * lands in one place.
   *
   * `op` mirrors the hub's command names verbatim (`compute_node_tools.py` and
   * the `ops` dispatch table), so the pair greps as a pair.
   */
  private async ops<T>(op: string, body?: Record<string, unknown>): Promise<T> {
    const action = new ActionInfo('ops', ComputeNode.type, this.id, 'POST');
    action.subpath = [op];
    if (body) action.bodyParameters = body;
    return dataManager.callAction<Record<string, unknown> | undefined, T>(action);
  }

  /**
   * Whether this node is a cloud SANDBOX — a box a person opens and works in,
   * as opposed to an agent's deployment box or the local machine.
   *
   * On the entity, not in a screen. `use-sandboxes.ts` used to answer it inline
   * by reading BOTH the provider AND a magic string out of the untyped
   * `node_config` blob, so every surface that wanted the question had to know
   * that blob's shape. The rule is one thing; it belongs in one place.
   *
   * Worth knowing before touching it: the
   * hub stopped reading `flavor` for TEMPLATE selection ("one family, one axis,
   * no client-derived input" — `setup_node`), which reads like the marker is
   * dead. It is not: the sandbox UI writes it at create time and nothing clears
   * it, so it remains the marker separating an interactive workspace from a
   * provider's other compute nodes.
   *
   * The provider field is read tolerantly because the hub spells it
   * `node_provider` and this entity types it `node_provider_type`.
   */
  get isSandbox(): boolean {
    const provider = (this as unknown as { node_provider?: string }).node_provider ?? this.node_provider_type;
    const flavor = (this.node_config as { flavor?: string } | undefined)?.flavor;
    return SANDBOX_PROVIDERS.has(provider ?? '') && flavor === WORKSPACE_FLAVOR;
  }

  // ── lifecycle ────────────────────────────────────────────────────────

  /** Start a provisioned node. */
  async startup(): Promise<void> {
    await this.ops<void>('startup');
  }

  /** Stop it for good — the provider machine goes away. */
  async shutdown(): Promise<void> {
    await this.ops<void>('shutdown');
  }

  /** Put it to sleep. Cheap to wake; the shared link wakes it on its own. */
  async pause(): Promise<void> {
    await this.ops<void>('pause');
  }

  /** Wake it. */
  async resume(): Promise<void> {
    await this.ops<void>('resume');
  }

  /**
   * What the backing machine is doing.
   *
   * Normalized server-side into one shape across providers, so this type is no
   * longer the union of one provider's fields with another's.
   */
  /** The agents placed on this machine (`compute_node/<id>/agents`), for its card. Hub-owned, so reflected. */
  async agents(): Promise<NodeAgent[]> {
    const info = new ActionInfo('agents', this.getType(), this.id, 'GET');
    info.hubReflect = true;
    const data = await dataManager.callAction<unknown, { agents?: NodeAgent[] }>(info);
    return data?.agents ?? [];
  }

  async status(): Promise<NodeStatus> {
    return this.ops<NodeStatus>('status');
  }

  /**
   * Bring the Flowpad app inside the box up, and sign it in.
   *
   * Returns whether it is healthy and who it ended up signed in as — the hub
   * reads that identity back from the box rather than reporting what it asked
   * for.
   */
  async workspaceReady(): Promise<WorkspaceReady> {
    return this.ops<WorkspaceReady>('workspace-ready');
  }

  /**
   * Upgrade the FlowPad app INSIDE the box to the latest published release, and
   * bring it back up: `flow stop`, `flow upgrade`, and the hub's own start.
   *
   * The equivalent of typing those commands into a terminal on the machine,
   * except that the start is the hub's workspace-ready path — the only one that
   * carries the hub url into the process — so a box does not come back pointed
   * at the image's default hub.
   *
   * One call per box, and it is slow by nature: it is a PyPI install followed by
   * an app boot, not a request. Owner-only, like every other `ops` command.
   */
  async upgradeApp(): Promise<AppUpgrade> {
    return this.ops<AppUpgrade>('upgrade-app');
  }

  /** Every service on this machine, checked now — each endpoint's result, in one report. */
  async healthCheck(): Promise<NodeHealth> {
    const action = new ActionInfo('health', ComputeNode.type, this.id, 'GET');
    return dataManager.callAction<undefined, NodeHealth>(action);
  }

  // ── setting a box's project up ───────────────────────────────────────
  //
  // The box commands underneath (validate, clone, index, attach, default…) are
  // the hub's control plane and are not reachable from here. A client asks for
  // the outcome; the hub runs the sequence.

  /**
   * Set the box up with its project and make it the one the box opens on —
   * the whole first-launch sequence, run by the hub. `steps` names each step's
   * outcome, and a failed call carries them too, so a checklist can say which
   * step broke.
   */
  async provisionProject(setup: ProvisionSetup): Promise<ProvisionResult> {
    return this.ops<ProvisionResult>('provision-project', setup as unknown as Record<string, unknown>);
  }

  /**
   * Sign the box out of the cloud, clearing the credentials stored ON it.
   *
   * Deliberately NOT the same thing as turning `auto_login` off. That also
   * revokes the node-bound API key, which is a change to how the box behaves
   * from now on; this is just "end the session that is running in there", and
   * leaves the setting alone. With `auto_login` on, the next open signs the box
   * straight back in — the correct behaviour for "log me out of it now".
   *
   * Signing the box out never touches the caller's OWN hub session.
   */
  async signOut(): Promise<unknown> {
    const action = new ActionInfo('sign-out', ComputeNode.type, this.id, 'POST');
    return dataManager.callAction<undefined, unknown>(action);
  }

  /**
   * Execute a shell command on this compute node.
   * @param input - ShellInputFlowData with command and session info
   * @returns Promise with ShellOutputFlowData result
   *
   * @example
   * ```typescript
   * const computeNode = await flow.getComputeNode();
   * const input = new ShellInputFlowData('ls -la', 'cmd_123', 'flow-shell');
   *
   * const output = await computeNode.executeCommand(input);
   * // output contains stdout, stderr, and exit_code
   * ```
   */
  async executeCommand(input: ShellInputFlowData): Promise<ShellOutputFlowData> {
    const action = new ActionInfo('ops', ComputeNode.type, this.id, 'POST', true);
    action.subpath = 'command';
    action.bodyParameters = {
      command: input.command,
      session_id: input.sessionId,
    };

    const response = await dataManager.callAction(action);
    const data = (response as Record<string, unknown>).data || response;

    if (!data) {
      throw new Error('No response from command execution');
    }

    // Parse XML response using FlowStreamProcessor
    const processor = new FlowStreamProcessor();
    const output = new ShellOutputFlowData();

    processor.on(FlowEvents.DATA_END, (flowData: FlowData) => {
      if (flowData.elementType === FlowElementTypes.SHELL_OUTPUT) {
        const channel = flowData.channel;
        const content = flowData.content;

        if (channel === 'stdout') {
          output.appendStdout(content);
        } else if (channel === 'stderr') {
          output.appendStderr(content);
        }

        // Check if this is the final chunk
        if (flowData.isFinal) {
          // A missing or non-numeric code is UNKNOWN, never success: defaulting to
          // '0' reported a command that never reported an exit (or never started)
          // as having exited cleanly. `exitCode` stays undefined.
          const raw = flowData.attributes['exit-code'];
          const exitCode = raw !== undefined && /^-?\d+$/.test(raw) ? parseInt(raw, 10) : undefined;
          output.markComplete(exitCode);
        }
      }
    });

    processor.process_chunk(data as string);

    return output;
  }

  /**
   * Execute a shell command with streaming output.
   * @param input - ShellInputFlowData with command and session info
   * @param onCmdProgress - Optional callback for progress updates
   * @returns Promise that resolves when command completes
   */
  async executeCommandStreaming(
    input: ShellInputFlowData,
    onCmdProgress?: (progress: ShellCmdProgress) => void,
  ): Promise<void> {
    const abortController = new AbortController();
    const action = new ActionInfo(
      'ops',
      ComputeNode.type,
      this.id,
      'POST',
      false, // isRawResponse
      true, // isStreaming
      abortController.signal,
    );
    action.subpath = 'command';
    action.bodyParameters = {
      command: input.command,
      session_id: input.sessionId,
      stream: true, // Enable streaming
    };

    // Use dataManager to get streaming response
    const response = await dataManager.callAction<typeof action.bodyParameters, Response>(action);

    if (!response) {
      throw new Error('No response from API');
    }

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    if (!response.body) {
      throw new Error('CMD Response body is null - server may not support streaming');
    }

    // Get reader for stream processing
    const reader: ReadableStreamDefaultReader<Uint8Array> = response.body.getReader();

    // ShellCommandProcessor processes and reports FlowData elements via progress callback
    await ShellCommandProcessor.processCmdStream(reader, onCmdProgress, abortController);
  }

  // ============================================================
  // Service Control Methods
  // ============================================================

  /**
   * Run a simple shell command and return stdout.
   * @param command - Shell command to execute
   * @returns stdout from the command
   */
  private async runShell(command: string): Promise<string> {
    const input = new ShellInputFlowData(command, 'service-control');
    const output = await this.executeCommand(input);
    return output.stdout;
  }

  /**
   * Get machine status (processes and network connections) from the compute node.
   * @returns MachineStatus with processes and network info
   */
  async getMachineStatus(): Promise<MachineStatus> {
    const action = new ActionInfo('get-machine-status', ComputeNode.type, this.id);
    return await dataManager.callAction<void, MachineStatus>(action);
  }

  /**
   * Get the process info for an ephemeral local service descriptor.
   * @returns ProcessInfo if service is running, null otherwise
   */
  async getArtifactProcess(service: ServiceRuntimeDescriptor): Promise<ProcessInfo | null> {
    if (!service.port) {
      throw new ServiceControlError('Service has no port defined', service.id || '', 'get');
    }

    const port = parseInt(service.port, 10);
    const status = await this.getMachineStatus();

    // Find network connection by port
    const connection = status.network.find((conn) => conn.port === port);
    if (!connection) {
      return null;
    }

    // Find process by PID
    const process = status.processes.find((proc) => proc.pid === connection.pid);
    return process || null;
  }

  /**
   * Stop the process running an ephemeral local service.
   * @returns ProcessInfo of the killed process
   * @throws ServiceControlError if service is not running or kill fails
   */
  async stopArtifactProcess(service: ServiceRuntimeDescriptor): Promise<ProcessInfo> {
    const process = await this.getArtifactProcess(service);
    if (!process) {
      throw new ServiceControlError(`Service on port ${service.port} is not running`, service.id || '', 'stop');
    }

    // Kill the process
    const killResult = await this.runShell(`kill ${process.pid}`);
    if (killResult.includes('No such process')) {
      throw new ServiceControlError(`Failed to kill process ${process.pid}: No such process`, service.id || '', 'stop');
    }

    return process;
  }

  /**
   * Start an ephemeral local service using its start command.
   * @param maxWaitMs - Maximum time to wait for service to start (default: 30000)
   * @param pollIntervalMs - Interval between status checks (default: 1000)
   * @returns ProcessInfo of the started process
   * @throws ServiceControlError if start_cmd is missing or service fails to start
   */
  async startArtifactProcess(
    service: ServiceRuntimeDescriptor,
    maxWaitMs: number = 30000,
    pollIntervalMs: number = 1000,
  ): Promise<ProcessInfo> {
    if (!service.start_cmd) {
      throw new ServiceControlError('Service has no start command defined', service.id || '', 'start');
    }

    if (!service.port) {
      throw new ServiceControlError('Service has no port defined', service.id || '', 'start');
    }

    // Run start command in background (nohup + &)
    const startCmd = `nohup ${service.start_cmd} > /dev/null 2>&1 &`;
    await this.runShell(startCmd);

    // Poll until service is running or timeout
    const startTime = Date.now();
    while (Date.now() - startTime < maxWaitMs) {
      const process = await this.getArtifactProcess(service);
      if (process) {
        return process;
      }
      await new Promise((resolve) => setTimeout(resolve, pollIntervalMs));
    }

    throw new ServiceControlError(`Service failed to start within ${maxWaitMs}ms`, service.id || '', 'start');
  }

  /**
   * Restart an ephemeral local service (stop then start).
   * @param maxWaitMs - Maximum time to wait for service to start (default: 30000)
   * @returns ProcessInfo of the new process
   * @throws ServiceControlError if stop or start fails
   */
  async restartArtifactProcess(service: ServiceRuntimeDescriptor, maxWaitMs: number = 30000): Promise<ProcessInfo> {
    // Stop if running (ignore error if not running)
    try {
      await this.stopArtifactProcess(service);
      // Wait a moment for port to be released
      await new Promise((resolve) => setTimeout(resolve, 500));
    } catch (error) {
      // Ignore "not running" errors, rethrow others
      if (error instanceof ServiceControlError && error.operation === 'stop') {
        // Service wasn't running, that's fine
      } else {
        throw error;
      }
    }

    // Start the service
    return this.startArtifactProcess(service, maxWaitMs);
  }

  // ============================================================
  // JSON File Operations
  // ============================================================

  /**
   * Read a JSON file from the compute node filesystem.
   * @param path - Absolute path to the JSON file
   * @returns Parsed JSON data
   */
  async getJsonFile<T = unknown>(path: string): Promise<T> {
    const action = new ActionInfo('get-json-file', ComputeNode.type, this.id, 'GET');
    action.queryParameters = { path };
    const response = await dataManager.callAction(action);
    // Extract data from ApiSuccessResponse wrapper
    const data = (response as Record<string, unknown>).data || response;
    return data as T;
  }

  /**
   * Write JSON data to a file on the compute node filesystem.
   * @param path - Absolute path to the JSON file
   * @param data - JSON data to write
   */
  async saveJsonFile(path: string, data: unknown): Promise<void> {
    await this.post('save-json-file', { path, data });
  }

  /**
   * Clear all in-memory PTY state on the server for this compute node.
   * Mimics a server restart: wipes session_manager, replay_buffer, and
   * provider _pty_sessions. Shell DB entities are untouched; _open_shell
   * will detect the dead PTY via is_pty_alive() on the next resume().
   * @returns number of sessions cleared
   */
  async resetPty(): Promise<number> {
    const response = await this.post<{ cleared: number }>('reset-pty');
    return (response as any)?.data?.cleared ?? 0;
  }

  /**
   * Get the current working directory (runs `pwd`).
   */
  async getCwd(): Promise<string> {
    const action = new ActionInfo('get-cwd', ComputeNode.type, this.id);
    const response = await dataManager.callAction<void, { cwd: string }>(action);
    return response?.cwd ?? '';
  }

  /**
   * Open a native OS folder-picker dialog and return the selected path.
   * Returns null if the user cancelled.
   */
  async openPathDialog(initialDir?: string, mode: 'folder' | 'file' = 'folder'): Promise<string | null> {
    const response = await this.post<{ path: string | null }>('pick-folder', {
      ...(initialDir ? { initial_dir: initialDir } : {}),
      mode,
    });
    return (response as any)?.path ?? null;
  }

  /**
   * Create a workdir-bound git ops helper for this compute node.
   * @param workDir - Absolute path to the working directory
   */
  git(workDir: string): GitWorkdir {
    return new GitWorkdir(workDir, this.id);
  }

  /** Which of a project's declared secrets this node may see: `{project_id: [ENV_VAR]}`.
   *  Value-free — the token IS the env var name. An ABSENT project key means
   *  ALL of that project's secrets (an uncurated node is unrestricted). */
  attached_secrets: Record<string, string[]> = {};

  private secretAction<T>(name: string, body: Record<string, unknown>): Promise<T | null> {
    const action = new ActionInfo(name, ComputeNode.type, this.id, 'POST');
    action.bodyParameters = body;
    return dataManager.callAction<unknown, T>(action) as Promise<T | null>;
  }

  /** Every declared secret on the project, flagged attached or not.
   *  `all_attached` is true when nothing has been curated yet, so the UI can
   *  show every row checked without pretending someone chose them. */
  async listAttachedSecrets(projectId: string): Promise<{
    project_id: string;
    all_attached: boolean;
    secrets: { env_var: string; attached: boolean }[];
  } | null> {
    return this.secretAction('list-attached-secrets', { project_id: projectId });
  }

  async attachSecret(projectId: string, envVar: string): Promise<void> {
    await this.secretAction('attach-secret', { project_id: projectId, env_var: envVar });
  }

  async detachSecret(projectId: string, envVar: string): Promise<void> {
    await this.secretAction('detach-secret', { project_id: projectId, env_var: envVar });
  }

  /** Attach everything the project declares RIGHT NOW — a snapshot, not a
   *  standing wildcard. */
  async attachAllSecrets(projectId: string): Promise<void> {
    await this.secretAction('attach-all-secrets', { project_id: projectId });
  }
}
